import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Ornament } from "../components/Ornament";

// PROMPT 4 PART 1 — details step. Reached from OnboardingWho, or directly from
// the "no storyteller yet" redirect on Day 1 (older shortcut, defaults to "someone").
const RELATIONSHIPS = [
  { key: "Nan", label: "Nan" },
  { key: "Grandad", label: "Grandad" },
  { key: "Grandma", label: "Grandma" },
  { key: "Grandpa", label: "Grandpa" },
  { key: "Mum", label: "Mum" },
  { key: "Dad", label: "Dad" },
  { key: "Sister", label: "Sister" },
  { key: "Brother", label: "Brother" },
  { key: "Partner", label: "Partner" },
  { key: "Aunt", label: "Aunt" },
  { key: "Uncle", label: "Uncle" },
  { key: "Friend", label: "Friend" },
  { key: "Other", label: "Other" },
];

export default function OnboardingStoryteller() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const mode = params.get("mode") === "myself" ? "myself" : "someone";
  const nextPath = params.get("next") || "/story-week/intro";
  const isSelf = mode === "myself";

  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [relationship, setRelationship] = useState("");
  const [relationshipOther, setRelationshipOther] = useState("");
  const [callName, setCallName] = useState("");
  const [callNameTouched, setCallNameTouched] = useState(false);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInput = useRef(null);

  // Prefill from existing storyteller or the account name
  useEffect(() => {
    const st = user?.storyteller;
    if (st) {
      setFirstName(st.first_name || "");
      setSurname(st.surname || "");
      setRelationship(st.relationship === "Self" ? "" : (st.relationship || ""));
      setRelationshipOther(st.relationship_other || "");
      setCallName(st.call_name || "");
      setPhotoUrl(st.photo_url || "");
    } else if (isSelf && user?.name) {
      const parts = user.name.split(" ");
      setFirstName(parts[0] || "");
      setSurname(parts.slice(1).join(" "));
    }
  }, [user, isSelf]);

  // Auto-fill call_name from the relationship label, unless the user has edited it
  useEffect(() => {
    if (isSelf) return;
    if (callNameTouched) return;
    if (!relationship || relationship === "Other") return;
    setCallName(relationship);
  }, [relationship, callNameTouched, isSelf]);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingPhoto(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/me/storyteller/photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotoUrl(data.url);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't upload that photo. Try another?");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const onSave = async () => {
    setError("");
    if (!firstName.trim()) { setError("Please add a first name."); return; }
    const rel = isSelf ? "Self" : relationship;
    if (!rel) { setError("Please pick who this person is to you."); return; }
    if (rel === "Other" && !relationshipOther.trim()) { setError("Please tell us how you know them."); return; }
    setSaving(true);
    try {
      await api.post("/me/storyteller", {
        first_name: firstName.trim(),
        surname: surname.trim(),
        relationship: rel,
        relationship_other: rel === "Other" ? relationshipOther.trim() : null,
        call_name: isSelf ? null : (callName.trim() || null),
        photo_url: photoUrl || null,
        self_story: isSelf,
      });
      await refresh();
      navigate(nextPath, { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't save that just now. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#FAF7F2" }} data-testid="onboarding-storyteller-page">
      <header className="max-w-2xl mx-auto px-6 pt-8">
        <Link to="/onboarding/storyteller" className="inline-flex items-center gap-2 text-[#1B2A4A] hover:text-[#B8973A] transition-colors"
              style={{ minHeight: 44, fontFamily: "Georgia, serif", fontSize: "15px" }}
              data-testid="onboarding-back">
          <ChevronLeft size={18} /> Back
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-10 sm:py-14">
        <p className="label-ui text-[11px] text-[#B8973A] tracking-[0.35em] text-center">✦ THE LIVING BOOK ✦</p>
        <Ornament className="my-6" />
        <h1 className="text-center text-[#111D33]"
            style={{ fontFamily: "Cormorant Garamond, Georgia, serif", fontWeight: 300,
                     fontSize: "clamp(2rem, 5vw, 2.75rem)", lineHeight: 1.15 }}
            data-testid="onboarding-heading">
          {isSelf ? "A little about you" : "Who will you be sitting down with?"}
        </h1>
        <p className="text-center mt-4 text-[#5C5548] italic"
           style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem", lineHeight: 1.6 }}>
          {isSelf
            ? "This is your book. Tell us the name to print on the cover."
            : "The Living Book works best together. Tell us about the person whose stories you'd love to keep."}
        </p>

        <div className="mt-12 space-y-8">
          {/* Photo */}
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 flex items-center justify-center overflow-hidden"
                 style={{ background: "#F0E9DA", border: "1px solid #E8E1D3", borderRadius: 4 }}>
              {photoUrl ? (
                <img src={photoUrl} alt="" className="w-full h-full object-cover" data-testid="storyteller-photo-preview" />
              ) : (
                <span className="label-ui text-[10px] text-[#B8973A] tracking-[0.2em] text-center">ADD<br/>PHOTO</span>
              )}
            </div>
            <div className="flex-1">
              <button type="button" onClick={() => fileInput.current?.click()} disabled={uploadingPhoto}
                      className="hairline-cream bg-white text-[#1B2A4A] px-4 py-2 hover:bg-[#F7F1E5] transition"
                      style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "13px", letterSpacing: "0.15em" }}
                      data-testid="storyteller-photo-btn">
                {uploadingPhoto ? "UPLOADING…" : (photoUrl ? "CHANGE PHOTO" : "ADD A PHOTO (OPTIONAL)")}
              </button>
              <input ref={fileInput} type="file" accept="image/*" onChange={onPhoto} className="hidden" data-testid="storyteller-photo-input" />
              <p className="mt-2 text-[13px] text-[#8C8278]" style={{ fontFamily: "Georgia, serif" }}>A face on the cover, if you'd like.</p>
            </div>
          </div>

          {/* First name */}
          <div>
            <label className="label-ui text-[11px] text-[#5C5548] tracking-[0.25em]" htmlFor="st-first">FIRST NAME</label>
            <input id="st-first" data-testid="storyteller-firstname" type="text" value={firstName}
                   onChange={(e) => setFirstName(e.target.value)}
                   className="mt-2 w-full bg-white hairline-cream px-4 py-3 text-[#111D33]"
                   style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "18px" }} />
          </div>

          {/* Surname */}
          <div>
            <label className="label-ui text-[11px] text-[#5C5548] tracking-[0.25em]" htmlFor="st-sur">FAMILY SURNAME <span className="text-[#8C8278] normal-case">(for the book cover)</span></label>
            <input id="st-sur" data-testid="storyteller-surname" type="text" value={surname}
                   onChange={(e) => setSurname(e.target.value)}
                   className="mt-2 w-full bg-white hairline-cream px-4 py-3 text-[#111D33]"
                   style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "18px" }} />
          </div>

          {/* Relationship + call_name — only when "someone I love" */}
          {!isSelf && (
            <div data-testid="storyteller-relation-block">
              <p className="label-ui text-[11px] text-[#5C5548] tracking-[0.25em]">WHO ARE THEY TO YOU?</p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {RELATIONSHIPS.map((r) => (
                  <button key={r.key} type="button" onClick={() => setRelationship(r.key)}
                          className={`px-3 py-3 hairline-cream transition ${relationship === r.key ? "bg-[#B8973A] text-[#FAF7F2]" : "bg-white text-[#1B2A4A] hover:bg-[#F7F1E5]"}`}
                          style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "14px", letterSpacing: "0.1em" }}
                          data-testid={`storyteller-relation-${r.key.toLowerCase()}`}>
                    {r.label}
                  </button>
                ))}
              </div>
              {relationship === "Other" && (
                <input type="text" placeholder="How do you know them?" value={relationshipOther}
                       onChange={(e) => setRelationshipOther(e.target.value)}
                       className="mt-3 w-full bg-white hairline-cream px-4 py-3 text-[#111D33]"
                       style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "16px" }}
                       data-testid="storyteller-relation-other" />
              )}

              {/* Call name — pre-filled from chip, editable */}
              <div className="mt-6">
                <label htmlFor="st-call" className="label-ui text-[11px] text-[#5C5548] tracking-[0.25em]">WHAT DO YOU CALL THEM?</label>
                <input id="st-call" type="text"
                       value={callName}
                       onChange={(e) => { setCallName(e.target.value); setCallNameTouched(true); }}
                       placeholder={relationship && relationship !== "Other" ? relationship : "e.g. Nanna, Granny Jean, Mama"}
                       className="mt-2 w-full bg-white hairline-cream px-4 py-3 text-[#111D33]"
                       style={{ borderRadius: 4, fontFamily: "Georgia, serif", fontSize: "18px" }}
                       data-testid="storyteller-call-name" />
                <p className="mt-2 text-[13px] text-[#8C8278]" style={{ fontFamily: "Georgia, serif" }}>
                  We'll use this everywhere in the app. The book cover uses their real first name and family surname.
                </p>
              </div>
            </div>
          )}

          {error && <p className="text-red-700" role="alert" style={{ fontFamily: "Georgia, serif", fontSize: "15px" }} data-testid="storyteller-error">{error}</p>}

          <div className="pt-4">
            <button onClick={onSave} disabled={saving}
                    className="btn-gold w-full sm:w-auto"
                    style={{ minHeight: 52, padding: "14px 32px", fontSize: "13px", letterSpacing: "0.2em" }}
                    data-testid="storyteller-save-btn">
              {saving ? "SAVING…" : "CONTINUE"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
